import { pageConfig } from "@/common/service/page-config";
import { planTypes } from '@/common/constants';
import {
  PRO_PLUS_SOFT_BUNDLE_NAME_LIST,
  BIZ_PLUS_SOFT_BUNDLE_NAME_LIST,
  PBX_SOFT_BUNDLE_NAME_LIST,
  SOFT_BUNDLE_NAME_INFO,
} from './constant';
import _ from 'lodash';
import { computedCallingPlans } from '@/common/service/computers/plans/pbx';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';

export const getSoftBundleConfiguration = () => {
  const { features } = pageConfig?.partnerReferral || {};
  return features?.softBundleConfig;
};

export const getCurrentSoftBundleName = () => {
  return pageConfig?.partnerReferral?.features?.softBundleConfig?.config?.bundleName;
}

// for soft bundle with pro + pbx, the plan type context is the plan type of the pro plus plan
export const getCurrentSoftBundlePlanTypeContext = () => {
  return pageConfig?.partnerReferral?.features?.softBundleConfig?.config?.planTypeContext;
}

// from soft bundle with pro + pbx, softBundleConfig field from jointsale op configuration
export const isSoftProPlusBundle = () => {
  const currentBundleName = getCurrentSoftBundleName();
  const currentPlanTypeContext = getCurrentSoftBundlePlanTypeContext();

  return PRO_PLUS_SOFT_BUNDLE_NAME_LIST.includes(currentBundleName)
  || currentPlanTypeContext === planTypes.ZONE_PRO_PLUS;
}

export const isSoftBundleBizPlus = () => {
  const currentBundleName = getCurrentSoftBundleName();
  const currentPlanTypeContext = getCurrentSoftBundlePlanTypeContext();

  return BIZ_PLUS_SOFT_BUNDLE_NAME_LIST.includes(currentBundleName)
  || currentPlanTypeContext === planTypes.ZOOM_ONE_BUNDLE;
}

// pbx + scheduler + ....
export const isSimilarPbxBundle = () => {
  const currentBundleName = getCurrentSoftBundleName();
  const currentPlanTypeContext = getCurrentSoftBundlePlanTypeContext();
  return PBX_SOFT_BUNDLE_NAME_LIST.includes(currentBundleName)
  || currentPlanTypeContext === planTypes.ZOOM_PHONE;
}

// contain pro plus(only for us/ca) and soft bundle pro plus(pro + zoom phone)
export const isSimilarProPlusBundle = () => {
  return isSoftProPlusBundle() || pageConfig?.editConfig?.planType === planTypes.ZONE_PRO_PLUS;
}

export const isSimilarBizPlusBundle = () => {
  return isSoftBundleBizPlus() || pageConfig?.editConfig?.planType === planTypes.ZOOM_ONE_BUNDLE;
}

export const getSoftBundleMessages = (bundleName) => {
  const info = {
    [SOFT_BUNDLE_NAME_INFO.clinical]: {
      name: 'softBundle.clinical.name',
      title: 'softBundle.clinical.title',
      description: 'softBundle.clinical.description',
      tips: 'softBundle.clinical.tips',
    },
    [SOFT_BUNDLE_NAME_INFO.brick_mortar]: {
      name: 'softBundle.brick_mortar.name',
      title: 'softBundle.brick_mortar.title',
      description: 'softBundle.brick_mortar.description',
      tips: 'softBundle.brick_mortar.tips',
    },
    [SOFT_BUNDLE_NAME_INFO.professional]: {
      name: 'softBundle.professional.name',
      title: 'softBundle.professional.title',
      description: 'softBundle.professional.description',
      tips: 'softBundle.professional.tips',
    },
  }

  return info[bundleName] ?? {};
}

export const softBundleUtils = {
  get bundleName() {
    return getCurrentSoftBundleName();
  },
  isClinical() {
    return this.bundleName === SOFT_BUNDLE_NAME_INFO.clinical;
  },
  isBrickMortar() {
    return this.bundleName === SOFT_BUNDLE_NAME_INFO.brick_mortar;
  },
  isProfessional() {
    return this.bundleName === SOFT_BUNDLE_NAME_INFO.professional;
  },
  isSimilarPbxBundle,
  isSimilarProPlusBundle,
  isSimilarBizPlusBundle,
  isSoftProPlusBundle,
  isSoftBundleBizPlus,
  hasCustomSorts() {
    return !_.isEmpty(getSoftBundleSorts());
  },
  getCustomSorts() {
    return getSoftBundleSorts();
  }
}

export const getSoftBundleSorts = () => {
  const softBundleConfig = getSoftBundleConfiguration();
  const sortList = softBundleConfig?.config?.sortList;
  if (!sortList) return null;

  // support sort list
  if (Array.isArray(sortList)) {
    return sortList;
  }

  return null;
};

export const forCurrentPlanShowInAddonForSoftBundle = (planType) => {
  const softBundleConfiguration = getSoftBundleConfiguration();
  return _.get(softBundleConfiguration, [planType, 'showInAddon'], false);
}

export const softBundleForAddon = {
  [planTypes.ZOOM_PHONE$ZPI]: {
    test: () => {
      return isSimilarBizPlusBundle() || isSimilarProPlusBundle() || isSimilarPbxBundle();
    }
  },
  [planTypes.ZOOM_PHONE$ADDITIONAL]: {
    test: (store) => {
      const bundlePlanList = _.values(store.getters.softBundleState?.bundlePlanMap);
      return _.some(computedCallingPlans(bundlePlanList), (p) => p.region === PBX_REGIONS.US_AND_CANADA);
    }
  }
}
